import CustomerSelector from '@/components/sales/CustomerSelector';
import DiscountCalculator from '@/components/sales/DiscountCalculator';
import ItemCalculator from '@/components/sales/ItemCalculator';
import UnitCalculator from '@/components/sales/UnitCalculator';
import useTicket from '@/hooks/sales/useTicketAttaches';
import { selectSalesItemsLayout, setSalesItemsLayout } from '@/redux/config/configSlice';
import {
  selectActiveTicket,
  selectCategories,
  selectOpenedTicket,
  selectShift,
} from '@/redux/data/dataSlice';
import { DiscountType, ItemType, ModifierOptionType, Unit } from '@/redux/data/dataTypes';
import toaster from '@/toaster';
import { getTicketTotal } from '@/utils/ticket';
import {
  SearchOutlined,
  ShoppingCartOutlined,
  TagsOutlined
} from '@ant-design/icons';
import { Badge, Button, Input, Tabs } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DiscountListWithWrapper from '../../components/discount/DiscountListWithWrapper';
import ItemsList from '../../components/items/ItemsList';
import ModifierListWithWrapper from '../../components/modifiers/ModifierListWithWrapper';
import LayoutToggle, { LayoutType } from '../../components/sales/LayoutToggle';
import OpenShiftLanding from '../../components/shift/OpenShiftLanding';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import MiniCartPreview from './MiniCartPreview';

const Sales = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const shift = useAppSelector(selectShift);
  const [activeTab, setActiveTab] = useState('ITEMS');
  const [itemCalculator, setItemCalculator] = useState<ItemType | null>(null);
  const [unitCalculator, setUnitCalculator] = useState<ItemType | null>(null);
  const [discountCalculator, setDiscountCalculator] = useState<DiscountType | null>(null);
  const openedTickets = useAppSelector(selectOpenedTicket);
  const activeTicket = useAppSelector(selectActiveTicket);
  const { addItem, addDiscount, addModifierOptions } = useTicket();
  const salesItemsLayout = useAppSelector(selectSalesItemsLayout);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const categories = useAppSelector(selectCategories);

  const handleItemPress = useCallback(
    (item: ItemType) => {
      if (!item.price || item.sellingType === 'WEIGHT') {
        return setItemCalculator(item);
      }

      if (item.sellingType === 'QUANTITY' && item.units && item.units.length > 0) {
        return setUnitCalculator(item);
      }

      addItem(item);
    },
    [addItem]
  );

  const handleSubmitItemCalculate = useCallback(
    (item: ItemType) => {
      setItemCalculator(null);
      addItem(item);
    },
    [addItem]
  );

  const handleSubmitDiscountCalculate = useCallback(
    (discount: DiscountType) => {
      setDiscountCalculator(null);
      addDiscount(discount).catch((err) => {
        toaster.error(t(err));
      });
    },
    [addDiscount, t]
  );

  const handleSubmitUnitCalculate = useCallback(
    (item: ItemType, selectedUnit?: Unit) => {
      setUnitCalculator(null);
      addItem(item, selectedUnit);
    },
    [addItem]
  );

  const handleDiscountPress = useCallback(
    (discount: DiscountType) => {
      if (!discount.value) return setDiscountCalculator(discount);
      addDiscount(discount).catch((err) => {
        toaster.error(t(err));
      });
    },
    [addDiscount, t]
  );

  const handleModifierOptionsSelect = useCallback(
    (options: ModifierOptionType[]) => {
      addModifierOptions(options);
    },
    [addModifierOptions]
  );

  const handleBacklogPress = useCallback(() => {
    navigate('/backlog');
  }, [navigate]);

  const handleLayoutChange = useCallback(
    (layout: LayoutType) => {
      dispatch(setSalesItemsLayout(layout));
    },
    [dispatch]
  );

  const total = useMemo(() => {
    const ticketTotal = getTicketTotal(activeTicket);
    return typeof ticketTotal === 'string' ? parseFloat(ticketTotal) : ticketTotal || 0;
  }, [activeTicket]);

  const leftComponent = useMemo(() => {
    if (!shift) {
      return (
        <div className="flex items-center">
          <span className="text-xl font-semibold text-white">{t('common:sales')}</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-4 min-w-max">
        <Button
          type="text"
          className="flex items-center gap-3 text-white hover:opacity-80 transition-opacity"
          disabled={!(openedTickets && openedTickets.length > 0)}
          onClick={handleBacklogPress}
        >
          <span className="text-xl font-semibold">{t('common:invoice')}</span>
          {openedTickets && openedTickets.length > 0 && (
            <Badge count={openedTickets.length} style={{ fontWeight: 'bold' }} />
          )}
        </Button>
        <div className="h-8 w-[1px] bg-white bg-opacity-30" />
        <div className="text-white">
          <div className="text-sm opacity-80">{t('common:active_shift')}</div>
          <div className="text-base font-medium">
            {new Date(shift.startDate).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        </div>
      </div>
    );
  }, [shift, openedTickets, handleBacklogPress, t]);

  const items = useMemo(
    () => [
      {
        key: 'ITEMS',
        label: t('common:items'),
        icon: <ShoppingCartOutlined />,
      },
      {
        key: 'DISCOUNTS',
        label: t('common:discounts'),
        icon: <TagsOutlined />,
      },
      // {
      //   key: 'MODIFIERS',
      //   label: t('common:modifiers'),
      //   icon: <AppstoreOutlined />,
      // },
    ],
    [t]
  );

  const renderList = useCallback(() => {
    switch (activeTab) {
      case 'ITEMS':
        return (
          <>
            <div className="space-y-3 mb-4">
              <Input
                id="item-search"
                placeholder={t('common:search_items')}
                prefix={<SearchOutlined className="text-gray-400" />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
                allowClear
              />

              <div className="flex items-center gap-2 flex-wrap pb-2 -mx-2 px-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                <Button
                  type={selectedCategory === null ? 'primary' : 'default'}
                  onClick={() => setSelectedCategory(null)}
                  className="flex-shrink-0"
                >
                  {t('common:all_items')}
                </Button>
                {categories?.map((category) => (
                  <Button
                    key={category.ID}
                    type={selectedCategory === category.ID ? 'primary' : 'default'}
                    onClick={() => setSelectedCategory(category.ID)}
                    className="flex-shrink-0 flex items-center gap-1"
                  >
                    <span
                      className="w-3 h-3 rounded-full inline-block flex-shrink-0"
                      style={{ backgroundColor: category.color }}
                    />
                    {category.name}
                  </Button>
                ))}
              </div>
            </div>

            <div className="flex justify-between items-center mb-3">
              <div className="text-sm text-gray-500 hidden sm:block">
                {searchQuery || selectedCategory
                  ? t('common:filtered_results')
                  : t('common:all_items')}
              </div>
              <LayoutToggle
                layout={salesItemsLayout}
                onChange={handleLayoutChange}
                className="mb-2"
              />
            </div>
            <ItemsList
              onItemPress={handleItemPress}
              layout={salesItemsLayout}
              searchTxt={searchQuery}
              selectedCategories={selectedCategory ? [selectedCategory] : []}
            />
          </>
        );
      case 'DISCOUNTS':
        return <DiscountListWithWrapper onItemPress={handleDiscountPress} />;
      case 'MODIFIERS':
        return <ModifierListWithWrapper onOptionsSelect={handleModifierOptionsSelect} />;
      default:
        return null;
    }
  }, [
    activeTab,
    handleDiscountPress,
    handleItemPress,
    handleModifierOptionsSelect,
    salesItemsLayout,
    handleLayoutChange,
    searchQuery,
    selectedCategory,
    categories,
    t,
  ]);

  if (itemCalculator) {
    return (
      <ItemCalculator
        item={itemCalculator}
        onClose={() => setItemCalculator(null)}
        onSubmit={handleSubmitItemCalculate}
      />
    );
  }

  if (discountCalculator) {
    return (
      <DiscountCalculator
        discount={discountCalculator}
        onClose={() => setDiscountCalculator(null)}
        onSubmit={handleSubmitDiscountCalculate}
      />
    );
  }

  if (unitCalculator) {
    return (
      <UnitCalculator
        item={unitCalculator}
        onClose={() => setUnitCalculator(null)}
        onSubmit={handleSubmitUnitCalculate}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="bg-primary p-4">{leftComponent}</div>
      <div className="p-5 flex-1">
        {shift ? (
          <div className="flex flex-col md:flex-row gap-4">
            <div className="w-full md:w-[calc(100%-320px)] space-y-4">
              <CustomerSelector />
              <Tabs items={items} activeKey={activeTab} onChange={setActiveTab} className="mb-4" />
              {renderList()}
            </div>

            {/* Mini Cart Preview */}
            <MiniCartPreview
              activeTicket={activeTicket}
              total={total}
              disableCharge={!activeTicket?.customerId}
            />
          </div>
        ) : (
          <OpenShiftLanding />
        )}
      </div>
    </div>
  );
};

export default Sales;
